// Hamilton College: public college page (#2652, round 2), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Hamilton's Common Data Set 2025-2026 (CDS 2025-26 Excel Final.pdf on
//    hamilton.edu, linked from the Office of Institutional Research's Common
//    Data Sets page): C1 applicants, admits and enrollees for Fall 2025 with
//    the residency table, C8 test policy for Fall 2027, C21/C22 plans (early
//    decision counts for the Fall 2025 entering class, no early action), and
//    G1/G5 2026-2027 costs. Quirk: every page header of the PDF also carries
//    a stray "Common Data Set 2024-2025" line under the 2025-2026 title; the
//    figures are labeled Fall 2025 and 2026-2027 throughout.
//  - hamilton.edu admissions: Apply (Important Deadlines, dated November 15,
//    2026 and January 4, 2027), First-Year Applicants (the late March
//    Regular Decision notification), Early Decision (decision dates and the
//    binding terms), and Testing Policy.
//  - Cross-check only (not linked on the page): Hamilton Admission, Tuition
//    (hamilton.edu/admission/tuition) gives the same 2026-27 direct costs
//    ($95,250) and books ($800), but personal expenses "up to $1,000" and
//    travel "up to $1,800" against the CDS's $1,000 and $500; the CDS
//    figures are used.
//  - College Scorecard API, school id 191515 (net price by family income).
// Departure: the CDS C14/C21 dates (January 6 closing, December 15 and
// February 10 early decision notifications) are the previous cycle's; the
// admissions pages for fall 2027 entry win.
// Nothing here is profile-derived, a fit score, anyone's odds, or a
// self-reported record. Re-check a figure against its source before editing
// it, and move `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Hamilton Common Data Set 2025-2026',
        url: 'https://www.hamilton.edu/documents/CDS%202025-26%20Excel%20Final.pdf',
        read: '2026-10-01',
    },
    apply: {
        label: 'Hamilton Admission, Apply: Important Deadlines',
        url: 'https://www.hamilton.edu/admission/apply',
        read: '2026-10-01',
    },
    firstYear: {
        label: 'Hamilton Admission, First-Year Applicants',
        url: 'https://www.hamilton.edu/admission/apply/first-year',
        read: '2026-10-01',
    },
    earlyDecision: {
        label: 'Hamilton Admission, Early Decision',
        url: 'https://www.hamilton.edu/admission/apply/early-decision',
        read: '2026-10-01',
    },
    testing: {
        label: 'Hamilton Admission, Testing Policy',
        url: 'https://www.hamilton.edu/admission/apply/testing',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?191515-Hamilton-College',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'hamilton-college',
    kbId: 'hamilton_college',
    name: 'Hamilton College',
    shortName: 'Hamilton',
    location: 'Clinton, NY',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Hamilton style guide colors); decoration only.
    branding: {
        primary: '#002F86',
        secondary: '#D6BA8B',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/hamilton_college_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 8904,
        admitted: 1210,
        enrolled: 478,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 1611, admitted: 334 },
            { group: 'Out-of-state', applied: 3707, admitted: 780 },
            { group: 'International', applied: 3586, admitted: 96 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Hamilton\'s Common Data Set reports one first-year acceptance rate for the whole college. Students choose a concentration (major) after they enroll, so there is no rate by major.',
        source: 'cds',
    },
    earlyRounds: {
        year: 'Fall 2025',
        title: 'Early decision',
        rows: [
            { plan: 'Early decision', applied: 1120, admitted: 315 },
        ],
        note: 'The Common Data Set gives one early decision count for the fall 2025 entering class; it does not split Early Decision I from Early Decision II. Hamilton has no early action plan.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 4' },
        rows: [
            { plan: 'Early Decision I', deadline: 'November 15, 2026', decision: 'By December 19', binding: true },
            { plan: 'Early Decision II', deadline: 'January 4, 2027', decision: 'By February 15', binding: true },
            { plan: 'Regular Decision', deadline: 'January 4, 2027', decision: 'Late March', binding: false },
        ],
        note: 'Regular Decision applicants may switch to Early Decision II by filing an Early Decision Agreement by 11:59 p.m. on February 1, 2027. Hamilton releases all decisions online, in the applicant portal.',
        source: 'apply',
        binding: 'Both Early Decision plans are binding; they differ only in timing. You, your parents and your counselor sign the Early Decision Agreement. If admitted, you withdraw all other college applications, file no new ones, and pay an enrollment deposit within two weeks. Students deferred to Regular Decision or offered January admission are released from the commitment, and Hamilton releases admitted students who find it unaffordable.',
        bindingSource: 'earlyDecision',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Hamilton does not require the SAT or ACT; sending scores is your choice. It superscores both tests and accepts scores you report yourself in the application. If you report your own scores and enroll, you send official results by July 1.',
        source: 'testing',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as not required for fall 2027 applicants, but considered if submitted, with scores due by January 15.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'firstYear',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 75210 },
            { item: 'Required fees', amount: 750 },
            { item: 'Housing (on campus)', amount: 10540 },
            { item: 'Food (on-campus meal plan)', amount: 8750 },
            { item: 'Books and supplies', amount: 800 },
            { item: 'Transportation', amount: 500 },
            { item: 'Other expenses', amount: 1000 },
        ],
        total: 97550,
        note: 'For a full-time student living on campus, fall and spring. Tuition, fees, housing and food come to $95,250.',
        source: 'cds',
        where: 'sections G1 and G5',
        netPrice: {
            average: 25793,
            byIncome: [
                { income: '$0 to $30,000', amount: 13679 },
                { income: '$30,001 to $48,000', amount: 4407 },
                { income: '$48,001 to $75,000', amount: 14749 },
                { income: '$75,001 to $110,000', amount: 20025 },
                { income: 'Over $110,000', amount: 40520 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Hamilton\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
